import React, { useState } from 'react';
import {
    Box, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Typography, Tooltip,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InboxIcon from '@mui/icons-material/Inbox';
import AppointmentHistoryView from './AppointmentHistoryView';

const ROWS_PER_PAGE_OPTIONS = [10, 15, 20, 50, 100];

function hasAnyHistory(row) {
    const c = row?.appointmentDeliveryHistoryCount ?? row?.appointmentDeliveryHistory?.length ?? 0;
    return c > 0 || !!(row?.appointmentDelivery && row?.appointmentDeliveryData);
}

export default function ExpandableAppointmentsTable({
    columns,
    rows,
    page,
    sizePerPage,
    setPage,
    setSizePerPage,
    rowCount,
    getRowId = (r) => r.id,
}) {
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const totalCols = columns.length;

    return (
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            {/*
              * tableLayout:fixed honours the explicit `width` on each header cell
              * (set per column in columns.js as percentages summing to ~100%).
              * Browser then distributes columns to fit the container — no
              * horizontal scroll. Cells with long unbreakable strings (AWB,
              * party names) clip via wordBreak / textOverflow.
              */}
            <TableContainer sx={{ overflowX: 'hidden' }}>
                <Table size='small' sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: '12.5px' } }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field}
                                    align={col.headerAlign || 'left'}
                                    sx={{
                                        py: 1.25,
                                        px: '8px',
                                        width: col.width || 'auto',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: '#555',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderBottom: '2px solid #e0e0e0',
                                        whiteSpace: 'normal',
                                        bgcolor: '#fafafa',
                                    }}
                                >
                                    {col.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={totalCols} sx={{ border: 0 }}>
                                    <Stack alignItems='center' justifyContent='center' gap={1} py={4}>
                                        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <InboxIcon sx={{ fontSize: 24, color: '#bdbdbd' }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e', fontWeight: 500 }}>
                                            No data available
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row) => {
                            const id = getRowId(row);
                            const isOpen = expanded.has(id);
                            const canExpand = hasAnyHistory(row);
                            return (
                                <React.Fragment key={id}>
                                    <TableRow
                                        sx={{
                                            bgcolor: isOpen ? '#f8f9ff' : '#fff',
                                            borderBottom: '1px solid #f0f0f0',
                                            '&:hover': { bgcolor: '#f8f9ff' },
                                        }}
                                    >
                                        {columns.map((col, colIdx) => {
                                            const cellInner = col.renderCell ? col.renderCell({ row }) : row?.[col.field];
                                            // Expand toggle inlined into first column instead of a dedicated
                                            // column — saves ~40px of horizontal space.
                                            const withToggle = colIdx === 0 ? (
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                    {canExpand ? (
                                                        <Tooltip title={isOpen ? 'Hide history' : 'Show appointment history'}>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => toggle(id)}
                                                                sx={{
                                                                    p: 0.25,
                                                                    mt: '-2px',
                                                                    transition: 'transform 0.2s',
                                                                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                }}
                                                            >
                                                                <KeyboardArrowRightIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Box sx={{ width: 22, flexShrink: 0 }} />
                                                    )}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>{cellInner}</Box>
                                                </Box>
                                            ) : cellInner;
                                            return (
                                                <TableCell
                                                    key={col.field}
                                                    align={col.align || 'left'}
                                                    sx={{
                                                        borderBottom: 'none',
                                                        verticalAlign: 'top',
                                                        px: '8px',
                                                        py: '8px',
                                                        width: col.width || 'auto',
                                                        maxWidth: col.width || 'none',
                                                        fontSize: '12px',
                                                        color: '#333',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {withToggle}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                    {canExpand && (
                                        <TableRow sx={{ bgcolor: '#f8f9ff' }}>
                                            <TableCell colSpan={totalCols} sx={{ p: 0, borderBottom: '1px solid #f5f5f5' }}>
                                                <Collapse in={isOpen} timeout='auto' unmountOnExit>
                                                    <AppointmentHistoryView rowData={row} />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component='div'
                count={rowCount ?? -1}
                page={page}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={sizePerPage}
                onRowsPerPageChange={(e) => { setSizePerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count >= Number.MAX_VALUE || count === -1 ? 'many' : count}`}
                sx={{
                    borderTop: '1px solid #e8e8e8',
                    bgcolor: '#fafafa',
                    '& .MuiTablePagination-toolbar': { minHeight: 44 },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.78rem', m: 0 },
                }}
            />
        </Box>
    );
}
